import { KioskSessionApi } from './kiosk-session-api';
import { TokenManager } from "@/core/auth/token-manager";
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  KioskSession,
  KioskSessionFilters
} from '../types/kiosk-session-types';
import {
  SessionsListRequest,
  SessionDetailRequest,
  SessionsListResponse,
  SessionDetailResponse,
  CountOnlyResponse
} from '../types/api-types';
import { getDefaultDateRange } from '../utils/date-utils';

/**
 * Repository for Kiosk Session data operations
 * Handles business logic, filtering, and coordination between API and UI
 * Follows the repository pattern established in the existing codebase
 */
export class KioskSessionRepository {
  constructor(
    private readonly api: KioskSessionApi,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Get operator ID from store (preferred) or auth headers (fallback)
   * Matches the pattern used in sessions-report-api.ts
   */
  private async getOperatorId(): Promise<string | null> {
    // PRIORITY 1: Check operator store (selected operator)
    const selectedOperator = useOperatorStore.getState().selectedOperator;
    if (selectedOperator?.operatorId) {
      return selectedOperator.operatorId.toString();
    }

    // PRIORITY 2: Fall back to JWT token
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const jwtOperatorId = authHeaders['X-Operator-Id'];
    if (jwtOperatorId && jwtOperatorId !== 'unknown') {
      return jwtOperatorId;
    }

    return null;
  }

  /**
   * Fetches kiosk sessions list with applied filters
   * Converts UI filters to API parameters and handles authentication
   */
  async getSessionsList(filters: KioskSessionFilters): Promise<SessionsListResponse | any[]> {
    // Get operator ID from store or JWT
    const operatorIdFromStore = await this.getOperatorId();

    // Check if we have authentication
    if (!operatorIdFromStore) {
      console.error('❌ No valid authentication found for kiosk sessions API');
      return {
        isSuccess: false,
        errorMessage: 'Authentication required',
        count: 0,
        rows: []
      };
    }

    // Use operatorId from filters if provided, otherwise use from store/JWT
    let operatorId = filters.operatorId;
    if (!operatorId) {
      operatorId = operatorIdFromStore;
    }
    
    // Build API request parameters
    const apiParams: SessionsListRequest = {
      operatorId: operatorId,
      from: filters.dateFrom,
      to: filters.dateTo,
      count: false // Get data rows only (separate count call handled by use case)
    };

    // Add optional location filter
    if (filters.locationId) {
      apiParams.locationId = filters.locationId;
    }

    // Add optional sub-operator filter (server-side); without one, include all sub-operators' sessions
    if (filters.subOperatorId) {
      apiParams.subOperatorId = filters.subOperatorId;
    } else {
      apiParams.includeSubOperators = true;
    }

    // Add optional limit for pagination
    if (filters.limit) {
      apiParams.limit = filters.limit;
    }

    // Add transaction ID filter (server-side fallback search)
    if (filters.transactionId !== undefined) {
      apiParams.transactionId = filters.transactionId;
    }

    // Add session ID filter (server-side fallback search)
    if (filters.sessionId !== undefined) {
      apiParams.sessionId = filters.sessionId;
    }

    // Add status filter (server-side)
    if (filters.statuses?.length) {
      apiParams.statuses = filters.statuses;
    }

    // Add settlement filter
    if (filters.settlement === 'no-settlement') {
      apiParams.noSettlement = true;
    } else if (filters.settlement === 'settled') {
      apiParams.noSettlement = false;
    }

    // NOTE: The Kiosk API does NOT support the 'skip' parameter for pagination.
    // Server-side skip-based pagination will not work correctly.
    // The API only supports 'limit' to control page size.
    // For proper pagination, use cursor-based pagination with lastSessionStartTime.
    if (filters.skip !== undefined) {
      console.warn(`⚠️ WARNING: Kiosk API does not support 'skip' parameter. Skip value ${filters.skip} will be ignored by the API.`);
      console.warn(`⚠️ Consider using cursor-based pagination (loadMoreSessions) instead of skip-based pagination.`);
      // Still send it in case API adds support in the future
      apiParams.skip = filters.skip;
    }

    try {
      const response = await this.api.getSessionsList(apiParams);
      
      // Apply additional client-side filtering if needed
      if (filters.last4Digits) {
        if (Array.isArray(response)) {
          // Handle raw array response
          const filteredArray = this.filterByLast4DigitsRawArray(response, filters.last4Digits);
          return filteredArray;
        } else {
          // Handle OData response
          response.rows = this.filterByLast4Digits(response.rows, filters.last4Digits);
          response.count = response.rows.length;
        }
      }

      return response;
    } catch (error) {
      console.error('❌ API Error fetching kiosk sessions:', error);
      throw new Error(`Failed to load kiosk sessions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Fetches detailed information for a specific session
   */
  async getSessionDetail(sessionId: string, operatorId?: string): Promise<SessionDetailResponse | any> {
    // Use provided operatorId if available, otherwise get from store/JWT
    let selectedOperatorId = operatorId;

    if (!selectedOperatorId) {
      selectedOperatorId = await this.getOperatorId() || undefined;
    }


    const apiParams: SessionDetailRequest = {
      operatorId: selectedOperatorId,
      sessionId
    };

    try {
      return await this.api.getSessionDetail(apiParams);
    } catch (error) {
      console.error(`❌ API Error fetching session detail for ${sessionId}:`, error);
      throw new Error(`Failed to load session details: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Gets default filters with current week date range
   */
  getDefaultFilters(): KioskSessionFilters {
    const defaultDateRange = getDefaultDateRange();
    
    return {
      dateFrom: defaultDateRange.from,
      dateTo: defaultDateRange.to,
      groupByLocation: false
    };
  }


  /**
   * Client-side filtering by last 4 digits of credit card
   * Applied after API response for additional refinement (OData format)
   */
  private filterByLast4Digits(sessions: KioskSession[], last4Digits: string): KioskSession[] {
    return sessions.filter(session => {
      if ((session as any).cardIdentifier) {
        return (session as any).cardIdentifier.includes(last4Digits);
      }
      const digits = parseInt(last4Digits);
      return !isNaN(digits) && session.last_4_digits === digits;
    });
  }

  /**
   * Client-side filtering by last 4 digits of credit card
   * Applied after API response for additional refinement (Raw array format)
   */
  private filterByLast4DigitsRawArray(rawSessions: any[], last4Digits: string): any[] {
    if (!last4Digits) return rawSessions;
    
    return rawSessions.filter(session => {
      if (session.cardIdentifier) {
        return session.cardIdentifier.includes(last4Digits);
      }
      const token = session.token;
      if (token && typeof token === 'string' && token.length >= 4) {
        return token.slice(-4) === last4Digits;
      }
      return false;
    });
  }

  /**
   * Gets the total count of sessions matching the filters
   * Separate call with count=true to get total number for pagination
   */
  async getSessionsCount(filters: KioskSessionFilters): Promise<number> {
    // Get operator ID from store or JWT
    const operatorIdFromStore = await this.getOperatorId();

    // Check if we have authentication
    if (!operatorIdFromStore) {
      console.error('❌ No valid authentication found for kiosk sessions count API');
      return 0;
    }

    // Use operatorId from filters if provided, otherwise use from store/JWT
    let operatorId = filters.operatorId;
    if (!operatorId) {
      operatorId = operatorIdFromStore;
    }

    // Build API request parameters for count only
    const apiParams: SessionsListRequest = {
      operatorId: operatorId,
      from: filters.dateFrom,
      to: filters.dateTo,
      count: true // Get count metadata only (no data rows)
    };

    // Add optional location filter
    if (filters.locationId) {
      apiParams.locationId = filters.locationId;
    }

    // Add optional sub-operator filter (server-side); without one, count all sub-operators' sessions
    if (filters.subOperatorId) {
      apiParams.subOperatorId = filters.subOperatorId;
    } else {
      apiParams.includeSubOperators = true;
    }

    // Add transaction ID filter (server-side fallback search)
    if (filters.transactionId !== undefined) {
      apiParams.transactionId = filters.transactionId;
    }

    // Add settlement filter
    if (filters.settlement === 'no-settlement') {
      apiParams.noSettlement = true;
    } else if (filters.settlement === 'settled') {
      apiParams.noSettlement = false;
    }

    // Add status filter
    if (filters.statuses?.length) {
      apiParams.statuses = filters.statuses;
    }

    try {
      const response = await this.api.getSessionsList(apiParams);

      // Extract count from response
      // API returns {count: number} when count=true
      if ('count' in response && typeof response.count === 'number') {
        return response.count;
      }

      // Legacy handling for older response formats
      if (typeof response === 'number') {
        console.warn('⚠️ WARNING: Unexpected number response, using as count');
        return response;
      }

      if (Array.isArray(response)) {
        // API returned array - this means count=true might not work properly
        console.warn('⚠️ WARNING: Count API returned an array, not a count object!');
        console.warn(`⚠️ Using array length (${response.length}) as fallback.`);
        return response.length;
      }

      // Fallback: use rows length if available
      if (response.rows && Array.isArray(response.rows)) {
        console.warn(`⚠️ WARNING: Count field not found, using rows.length (${response.rows.length}) as fallback.`);
        return response.rows.length;
      }

      console.error('❌ CRITICAL: No count or rows found in response!');
      console.error('❌ Response structure:', response);
      console.error('❌ Returning 0 - pagination will not work.');
      return 0;
    } catch (error) {
      console.error('❌ API Error fetching sessions count:', error);
      return 0;
    }
  }

  /**
   * Checks if the current user has the required authentication token
   * Returns false if authentication is required
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      // Check for operator ID from store or JWT
      const operatorId = await this.getOperatorId();
      if (!operatorId) {
        return false;
      }

      // Check for authorization header
      const authHeaders = await this.tokenManager.getAuthHeaders();
      const hasAuthHeader = !!authHeaders['authorization'];

      // We're authenticated if we have both an operator ID and an authorization header
      return hasAuthHeader;
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }
}